import { linkToAnonymousCourseDiscoveryPage } from "features/anonymous-pages/course-discovery/links";
import { linkToAnonymousResourcesPage } from "features/anonymous-pages/resources/links";
import lazyWithPreload from "features/app/components/lazyWithPreload";
import SelfDiagnosisPage from "features/app/diagnosis/DiagnosisPage";
import LoadableClassroom from "features/classroom/routes";
import { linkToClassroom } from "features/classroom/routes/links";
import { PageNotFoundError } from "features/common/components/AppErrors";
import { SplashScreenForLoadableChunk } from "features/common/loadingScreens/components/SplashScreen";
import {
  browserVersionIsNotSupported,
  BrowserVersionNotSupportedModal
} from "features/common/modals/components/DeviceWarningModal";
import RedirectToAppropriateHomepage from "features/common/routing/RedirectToAppropriateHomePage";
import SignedInRoute from "features/common/routing/SignedInRoute";

// This unused import improves the code split NavBar load time
import "features/dashboard/features/navbar/dashboard-navbar";

import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { CompatRoute, useLocation } from "react-router-dom-v5-compat";

import { useSelectedSchool } from "seneca-common/features/schools/school-selection/hooks";
import {
  credentialsRefreshHasErrored,
  isSignedIn
} from "seneca-common/features/user/state";
import { getReferralCodeFromLocation } from "seneca-common/utils/functions/referral";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";
import loaderWithRetry from "seneca-common/utils/loaderWithRetry";
import { useTypedState } from "seneca-common/utils/routing/react-router-typesafe-routes/dom";
import { analyticsReferredUserLandedOnSite } from "seneca-common/utils/senecaAnalytics";

import { linkToDashboard } from "features/dashboard/links";
import useRedirectToSelectAccountType from "features/dashboard/pages/hooks/useRedirectToSelectAccountType";
import DashboardRoute from "features/dashboard/routing/DashboardRoute";
import { useFeatureFlag } from "features/feature-flags";
import LoadableParentPlatform from "features/parents/routes";
import SignInParentViaLinkRoute from "features/passwordless-sign-in/parent/SignInParentViaLinkRoute";
import { useIsPlurallApp } from "features/plurall";
import { linkToMobileAuthRoute } from "features/pwa/mobile/auth/links";
import { isNativeAppiOS } from "features/pwa/mobile/ios/utils/isNativeAppiOS";
import { linkToCompleteAccountType } from "features/sign-up/complete-account/links";
import {
  linkToPasswordReset,
  linkToSignIn,
  linkToSignUp,
  linkToSignUpInvite
} from "features/sign-up/links";
import { linkToGetPremium } from "features/subscriptions/routes/get-premium/links";
import TeacherPlatform from "features/teachers/routes";
import { getShowPlurallHelpCentreModalOpen } from "features/ui-state/selectors";
import { MODAL_ROUTES, ROUTES } from "routing/routes";

import { linkToTutorLandingPage_deprecated } from "./features/resource-discover/routes/links";
import { ModalRoutes } from "./ModalRoutes";

const LoadableCreation = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "creation-chunk" */
        "features/course-creation/routes/Route"
      )
  )
);

const SignUpParentRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-parent-chunk" */ "features/sign-up/common-routes/components/SignUpParentRoute"
      )
  )
);

const LoginSchoolRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "login-school-chunk" */ "features/sign-up/login-school/LoginSchoolRoute"
      )
  )
);

const LoadableDashboardRouter = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "dashboard-router-chunk" */ "features/dashboard/pages/DashboardRouter"
      )
  )
);

const LoadableFrontDoor = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "front-door-chunk" */ "features/sign-up/components/FrontDoor"
      )
  )
);

const SignUpInviteRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-up-invite-chunk" */
        "features/sign-up/common-routes/components/SignUpInviteRoute"
      )
  )
);

const CompleteAccountTypeModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "complete-account-type-modal-chunk" */
        "features/sign-up/complete-account/CompleteAccountTypeModal/CompleteAccountTypeModal"
      )
  )
);

export const PreloadableGetPremiumRoute = lazyWithPreload(
  () =>
    import(
      /* webpackChunkName: "get-premium-chunk" */ "features/subscriptions/routes/get-premium/Route"
    )
);

const LoadableAnonymousCourseDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-course-discovery-chunk" */
        "features/anonymous-pages/course-discovery/Route"
      )
  )
);

const LoadableAnonymousResourcesHubDiscovery = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub-chunk" */
        "features/anonymous-pages/resources/Route"
      )
  )
);

const LoadablePlurallHelpCentreModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "plurall-help-centre-modal-chunk" */
        "features/plurall/components/PlurallHelpCentreModal/PlurallHelpCentreModal"
      )
  )
);

const LoadableResourceDiscover = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-resources-hub" */
        "features/resource-discover/routes/Route"
      )
  )
);

const MobileAuthRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mobile-auth" */
        "features/pwa/mobile/auth/screens/Route"
      )
  )
);

const LoadableCredentialsExpiredModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "credentials-expired-modal" */
        "features/common/components/CredentialsExpiredModal"
      )
  )
);

const LoadableAnonymousUnsubscribePageRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "anonymous-unsubscribe-page" */
        "features/anonymous-pages/unsubscribe/Route"
      )
  )
);

const LoadableUpdateAppPage = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "update-app-page" */
        "features/app/update/UpdateAppPage"
      )
  )
);

const AnonymousTutoringRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "tutoring-page" */
        "features/anonymous-pages/tutoring-page/Route"
      )
  )
);

const SignInCheckYourEmailRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-in-check-your-email-page" */
        "features/passwordless-sign-in/SignInCheckYourEmailRoute"
      )
  )
);

const SignInViaLinkRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "sign-in-via-link-page" */
        "features/passwordless-sign-in/SignInViaLinkRoute"
      )
  )
);

const LoadableAnonymousWhatIsPremium = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "creation-chunk" */
        "features/anonymous-pages/what-is-premium/Route"
      )
  )
);

export default function Routes() {
  const location = useLocation();
  const { background } = useTypedState(MODAL_ROUTES);
  const history = useHistory();
  const refreshCredentialsError = useSelector(credentialsRefreshHasErrored);
  const showPlurallHelpCentreModal = useSelector(
    getShowPlurallHelpCentreModalOpen
  );
  const signedIn = useSelector(isSignedIn);
  const enableSchoolFetch = !!useFeatureFlag("rootSchoolInfoFetchForAnalytics");
  const isPlurallApp = useIsPlurallApp();

  // TODO - temporary fix to populate data layer with global school properties
  useSelectedSchool({
    select: school => school?.schoolInfo,
    enabled: enableSchoolFetch && signedIn
  });

  useEffectOnMount(() => {
    logReferralCode(location);

    if (window.Cypress) {
      window.__routerHistory__ = history;
    }
  });

  useRedirectToSelectAccountType();

  if (browserVersionIsNotSupported()) {
    return <BrowserVersionNotSupportedModal />;
  }

  const baseLocation = background
    ? {
        ...background,
        state: { ...background.state, isBackground: true }
      }
    : location;

  return (
    <>
      <Suspense fallback={<SplashScreenForLoadableChunk />}>
        <Switch location={baseLocation}>
          <CompatRoute path={ROUTES.UPDATE.path}>
            <LoadableUpdateAppPage />
          </CompatRoute>
          <CompatRoute path={ROUTES.DIAGNOSIS.path}>
            <SelfDiagnosisPage />
          </CompatRoute>
          <Route path={linkToSignUp()} component={LoadableFrontDoor} />
          <Route path={linkToSignIn()} component={LoadableFrontDoor} />
          <Route path={linkToPasswordReset()} component={LoadableFrontDoor} />
          <Route path={linkToCompleteAccountType()}>
            <CompleteAccountTypeModal />
          </Route>
          <Route path={linkToSignUpInvite()}>
            <SignUpInviteRoute />
          </Route>
          <CompatRoute path={ROUTES.SIGN_UP_PARENT.path}>
            <SignUpParentRoute />
          </CompatRoute>
          <CompatRoute path={ROUTES.LOGIN_SCHOOL.path}>
            <LoginSchoolRoute />
          </CompatRoute>

          <CompatRoute path={ROUTES.SIGN_IN_PARENT_VIA_LINK.path}>
            <SignInParentViaLinkRoute />
          </CompatRoute>

          <CompatRoute path={ROUTES.SIGN_IN_CHECK_YOUR_EMAIL.path}>
            <SignInCheckYourEmailRoute />
          </CompatRoute>

          <CompatRoute path={ROUTES.SIGN_IN_VIA_LINK.path}>
            <SignInViaLinkRoute />
          </CompatRoute>

          {isPlurallApp ? (
            <Route path={linkToClassroom()}>
              <SignedInRoute>
                <LoadableClassroom />
              </SignedInRoute>
            </Route>
          ) : (
            <Route path={linkToClassroom()}>
              <LoadableClassroom />
            </Route>
          )}
          <CompatRoute path={ROUTES.CREATION.path}>
            <SignedInRoute>
              <LoadableCreation />
            </SignedInRoute>
          </CompatRoute>

          <Route path={linkToGetPremium()}>
            <PreloadableGetPremiumRoute />
          </Route>

          <Route path={linkToDashboard()}>
            <DashboardRoute>
              <LoadableDashboardRouter />
            </DashboardRoute>
          </Route>

          <CompatRoute path={ROUTES.TEACHER.path}>
            <SignedInRoute>
              <TeacherPlatform />
            </SignedInRoute>
          </CompatRoute>

          <CompatRoute path={ROUTES.PARENT.path}>
            <SignedInRoute>
              <LoadableParentPlatform />
            </SignedInRoute>
          </CompatRoute>

          <CompatRoute path={ROUTES.WHAT_IS_PREMIUM.path}>
            <LoadableAnonymousWhatIsPremium />
          </CompatRoute>

          {!isPlurallApp && (
            <Route path={linkToAnonymousCourseDiscoveryPage()}>
              <LoadableAnonymousCourseDiscovery />
            </Route>
          )}

          {!isPlurallApp && (
            <Route path={linkToAnonymousResourcesPage()}>
              <LoadableAnonymousResourcesHubDiscovery />
            </Route>
          )}

          <CompatRoute path={ROUTES.UNSUBSCRIBE.path}>
            <LoadableAnonymousUnsubscribePageRoute />
          </CompatRoute>

          {!isPlurallApp && (
            <CompatRoute path={ROUTES.RESOURCE_DISCOVER.path}>
              <LoadableResourceDiscover />
            </CompatRoute>
          )}

          {!isPlurallApp && (
            <Route path={linkToTutorLandingPage_deprecated()}>
              <Redirect
                to={{
                  ...location,
                  pathname: ROUTES.RESOURCE_DISCOVER.buildPath({})
                }}
              />
            </Route>
          )}

          {/* TODO added temporary default error page in case seneca links were missed. Remove once all links are replaced for SOMOS app */}
          {isPlurallApp && (
            <CompatRoute path={ROUTES.NOT_FOUND.path}>
              <PageNotFoundError />
            </CompatRoute>
          )}

          {isNativeAppiOS() && (
            <Route path={linkToMobileAuthRoute()}>
              <MobileAuthRoute />
            </Route>
          )}

          {!isPlurallApp && (
            <CompatRoute path={ROUTES.TUTORING.path}>
              <AnonymousTutoringRoute />
            </CompatRoute>
          )}
          <RedirectToAppropriateHomepage />
        </Switch>
      </Suspense>
      {refreshCredentialsError && (
        <Suspense>
          <LoadableCredentialsExpiredModal />
        </Suspense>
      )}
      {isPlurallApp && showPlurallHelpCentreModal && (
        <Suspense>
          <LoadablePlurallHelpCentreModal />
        </Suspense>
      )}
      <ModalRoutes />
    </>
  );
}

function logReferralCode(location: any) {
  const referral = location && getReferralCodeFromLocation(location);

  referral && analyticsReferredUserLandedOnSite(referral, location.pathname);
}
